/**
 * O bloco "Passagem de plantão" (`TP-069`, `D9`, `D11`), no detalhe do Caso autorizado. No topo, o Processo Inicial
 * (presente ou a montar). Sem passagem: as divergências que a conferência do pedido × IW sugeriu, para a pessoa
 * confirmar, apagar ou acrescentar, e "Registrar que recebi". Com passagem: de quem para quem, quando, a conferência e
 * as divergências, com "Guia corrigida" em cada aberta e "Validar passagem" para quem é de fora. Nada aqui bloqueia o
 * Caso.
 */

import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ErroDaApi } from '../../../shared/api/erros';
import { chavesDeRetornos } from '../../../shared/retornos/api';
import { chavesDeCasos } from '../api/casos';
import { formatarDataHora } from '../../../shared/dominio/datas';
import { EstadoDeCarregamento } from '../../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../../shared/ui/EstadoDeErro';
import { Painel } from '../../../shared/ui/primitivas';
import { chavesDaPassagem, marcarGuiaCorrigida, passagemDoCaso, registrarPassagem, validarPassagem } from './api';
import {
  GRUPOS_DO_ITEM,
  LINHA_VAZIA,
  ROTULOS_DO_GRUPO,
  TAMANHO_MAXIMO_DO_TEXTO,
  TEXTO_SEM_CONFERENCIA,
  linhaInvalida,
  type Conferencia,
  type DivergenciaDaPassagem,
  type GrupoDoItem,
  type ItemDoProcessoInicial,
  type LinhaDeDivergencia,
  type PassagemDePlantao as Passagem,
  type PassagemDoCaso,
} from './tipos';

function mensagemDoErro(erro: unknown, padrao: string): string {
  return erro instanceof ErroDaApi ? erro.message : padrao;
}

/**
 * O que mostra o Caso relê: detalhe (dono), linha do tempo (evento novo), as listas de casos e de captação e os
 * retornos (filtrados pelo dono). A passagem NÃO: a escrita já devolveu a passagem atualizada, e relê-la junto a
 * sobrescreveria com uma leitura concorrente.
 */
function releOCaso(cliente: QueryClient, casoId: string): void {
  const passagem = chavesDaPassagem.doCaso(casoId);
  void cliente.invalidateQueries({
    predicate: (q) =>
      (q.queryKey[0] === chavesDeCasos.raiz[0] || q.queryKey[0] === chavesDeRetornos.raiz[0]) &&
      !(q.queryKey.length === passagem.length && q.queryKey.every((parte, i) => parte === passagem[i])),
  });
}

/** Perdeu para outra pessoa, ou o Caso mudou: a passagem relê, para a tela mostrar o que vale agora. */
const CODIGOS_DE_ESTADO_VELHO = new Set(['HANDOFF_ALREADY_REGISTERED', 'HANDOFF_SELF', 'HANDOFF_CASE_NOT_AUTHORIZED']);

function LinhaDoFormulario({
  indice,
  linha,
  aoMudar,
  aoRemover,
}: {
  indice: number;
  linha: LinhaDeDivergencia;
  aoMudar: (linha: LinhaDeDivergencia) => void;
  aoRemover: () => void;
}): React.JSX.Element {
  const longo = (texto: string) => texto.trim().length > TAMANHO_MAXIMO_DO_TEXTO;
  return (
    <li className="flex flex-wrap items-start gap-2" aria-label={`Divergência ${String(indice + 1)}`}>
      <TextField
        select
        size="small"
        label="Grupo"
        value={linha.grupo}
        onChange={(e) => {
          aoMudar({ ...linha, grupo: e.target.value as GrupoDoItem });
        }}
        sx={{ flex: '0 1 13rem' }}
      >
        {GRUPOS_DO_ITEM.map((g) => (
          <MenuItem key={g} value={g}>
            {ROTULOS_DO_GRUPO[g]}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        label="Campo"
        value={linha.campo}
        onChange={(e) => {
          aoMudar({ ...linha, campo: e.target.value });
        }}
        error={longo(linha.campo)}
        sx={{ flex: '1 1 10rem' }}
      />
      <TextField
        size="small"
        label="No pedido"
        value={linha.noPedido}
        onChange={(e) => {
          aoMudar({ ...linha, noPedido: e.target.value });
        }}
        error={longo(linha.noPedido)}
        sx={{ flex: '1 1 8rem' }}
      />
      <TextField
        size="small"
        label="No IW"
        value={linha.noIw}
        onChange={(e) => {
          aoMudar({ ...linha, noIw: e.target.value });
        }}
        error={longo(linha.noIw)}
        sx={{ flex: '1 1 8rem' }}
      />
      <Button size="small" onClick={aoRemover}>
        {linha.sugerida ? 'Apagar' : 'Remover'}
      </Button>
    </li>
  );
}

function FormularioDaPassagem({ casoId, dados }: { casoId: string; dados: PassagemDoCaso }): React.JSX.Element {
  const cliente = useQueryClient();
  const temSugestoes = dados.sugestoes.length > 0;
  const conferiu = dados.conferenciaAutomatica !== null;
  // Com sugestões, começa "com divergência" e com as linhas delas; a pessoa confere cada uma (`D3`). Conferência que
  // rodou e não achou nada começa "sem divergência". Sem conferência, a pessoa escolhe.
  const inicial: Conferencia | '' = temSugestoes ? 'com_divergencia' : conferiu ? 'sem_divergencia' : '';
  const [conferencia, setConferencia] = useState<Conferencia | ''>(inicial);
  const [linhas, setLinhas] = useState<readonly LinhaDeDivergencia[]>(temSugestoes ? dados.sugestoes : [LINHA_VAZIA]);

  const registrar = useMutation({
    mutationFn: () =>
      registrarPassagem(casoId, conferencia as Conferencia, linhas, {
        versao: dados.conferenciaAutomatica,
        sugeridas: dados.sugestoes.length,
      }),
    onSuccess: (atualizada) => {
      cliente.setQueryData(chavesDaPassagem.doCaso(casoId), atualizada);
      releOCaso(cliente, casoId);
    },
    onError: (erro) => {
      if (erro instanceof ErroDaApi && erro.codigo !== null && CODIGOS_DE_ESTADO_VELHO.has(erro.codigo)) {
        void cliente.invalidateQueries({ queryKey: chavesDaPassagem.doCaso(casoId) });
      }
    },
  });

  const comDivergencia = conferencia === 'com_divergencia';
  const faltaItem = comDivergencia && (linhas.length === 0 || linhas.some(linhaInvalida));
  const podeEnviar = conferencia !== '' && !faltaItem && !registrar.isPending;

  return (
    <div className="space-y-3">
      <p role="status" className="m-0 text-[0.8125rem] text-[var(--tinta-2)]">
        {temSugestoes
          ? `O pedido e o IW dizem coisas diferentes em ${String(dados.sugestoes.length)} ${dados.sugestoes.length === 1 ? 'campo' : 'campos'}. Confira cada um: confirme, edite, apague ou acrescente.`
          : conferiu
            ? 'O pedido e o IW batem nos campos conferidos (paciente e admissão).'
            : `${dados.semConferencia !== null ? TEXTO_SEM_CONFERENCIA[dados.semConferencia] : 'A conferência automática não rodou.'} Dá para registrar a passagem e escrever as divergências à mão.`}
      </p>

      <TextField
        select
        size="small"
        label="Conferência do pedido com o IW"
        value={conferencia}
        onChange={(e) => {
          setConferencia(e.target.value as Conferencia);
        }}
        sx={{ minWidth: '18rem' }}
      >
        <MenuItem value="sem_divergencia">Sem divergência</MenuItem>
        <MenuItem value="com_divergencia">Com divergência</MenuItem>
      </TextField>

      {comDivergencia ? (
        <div className="space-y-2">
          <ul aria-label="Divergências a registrar" className="m-0 list-none space-y-2 pl-0">
            {linhas.map((linha, i) => (
              <LinhaDoFormulario
                // A linha não tem id antes de gravar; a posição é a identidade dela no formulário.
                key={i}
                indice={i}
                linha={linha}
                aoMudar={(nova) => {
                  setLinhas(linhas.map((l, j) => (j === i ? nova : l)));
                }}
                aoRemover={() => {
                  setLinhas(linhas.filter((_, j) => j !== i));
                }}
              />
            ))}
          </ul>
          <Button
            size="small"
            onClick={() => {
              setLinhas([...linhas, LINHA_VAZIA]);
            }}
          >
            Acrescentar divergência
          </Button>
          {faltaItem ? (
            <p className="m-0 text-[0.75rem] text-[var(--tinta-2)]">
              Com divergência, diga ao menos um campo, com até {TAMANHO_MAXIMO_DO_TEXTO} caracteres em cada texto.
            </p>
          ) : null}
        </div>
      ) : null}

      <Button
        variant="outlined"
        size="small"
        disabled={!podeEnviar}
        onClick={() => {
          registrar.mutate();
        }}
      >
        Registrar que recebi
      </Button>

      {registrar.isError ? (
        <p role="alert" className="m-0 text-[0.8125rem] text-[var(--roxo)]">
          {mensagemDoErro(registrar.error, 'Não foi possível registrar. Tente novamente.')}
        </p>
      ) : null}
    </div>
  );
}

function resumoDaConferencia(p: Passagem): string {
  if (p.conferencia === 'sem_divergencia') return 'Conferência sem divergência.';
  const abertas = p.divergencias.filter((d) => d.aberta).length;
  const total = p.divergencias.length;
  const plural = total === 1 ? 'divergência' : 'divergências';
  return abertas === 0
    ? `${String(total)} ${plural}, todas com a guia corrigida.`
    : `${String(total)} ${plural}, ${String(abertas)} ${abertas === 1 ? 'aberta' : 'abertas'} com a captação.`;
}

function LinhaDaDivergencia({
  divergencia,
  aoCorrigir,
  corrigindo,
}: {
  divergencia: DivergenciaDaPassagem;
  aoCorrigir: () => void;
  corrigindo: boolean;
}): React.JSX.Element {
  const lados = [
    `Pedido: ${divergencia.noPedido ?? '(não traz)'}`,
    `IW: ${divergencia.noIw ?? '(não tem)'}`,
  ];
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-2 text-[0.8125rem]">
      <div>
        <strong>{ROTULOS_DO_GRUPO[divergencia.grupo]}</strong> · {divergencia.campo}
        {lados.length > 0 ? <span className="text-[var(--tinta-2)]"> · {lados.join(' · ')}</span> : null}
        {!divergencia.aberta ? (
          <div className="text-[var(--tinta-2)]">
            Guia corrigida
            {divergencia.corrigidaEm !== null ? ` em ${formatarDataHora(divergencia.corrigidaEm)}` : ''}
            {divergencia.corrigidaPorNome !== null ? ` por ${divergencia.corrigidaPorNome}` : ''}
          </div>
        ) : null}
      </div>
      {divergencia.aberta ? (
        <Button size="small" variant="outlined" disabled={corrigindo} onClick={aoCorrigir}>
          Guia corrigida
        </Button>
      ) : null}
    </li>
  );
}

function PassagemRegistrada({ casoId, dados, passagem }: { casoId: string; dados: PassagemDoCaso; passagem: Passagem }): React.JSX.Element {
  const cliente = useQueryClient();
  const atualizar = (atualizada: PassagemDoCaso) => {
    cliente.setQueryData(chavesDaPassagem.doCaso(casoId), atualizada);
    releOCaso(cliente, casoId);
  };

  const validar = useMutation({ mutationFn: () => validarPassagem(casoId), onSuccess: atualizar });
  const corrigir = useMutation({
    mutationFn: (divergenciaId: string) => marcarGuiaCorrigida(casoId, divergenciaId),
    onSuccess: atualizar,
  });
  // Uma mensagem de erro por vez, a do último gesto: o erro velho de um não esconde o novo do outro.
  const [ultimoGesto, setUltimoGesto] = useState<'validar' | 'corrigir' | null>(null);
  const erro = ultimoGesto === 'validar' ? validar.error : ultimoGesto === 'corrigir' ? corrigir.error : null;

  const de = passagem.deNome !== null ? ` de ${passagem.deNome}` : '';
  const para = passagem.paraNome ?? 'quem recebeu';

  return (
    <div className="space-y-3">
      <div className="text-[0.8125rem]">
        <p className="m-0">
          Recebido por <strong>{para}</strong>
          {de} em {formatarDataHora(passagem.registradaEm)}. {resumoDaConferencia(passagem)}
        </p>
        <p className="m-0 text-[var(--tinta-2)]">
          {passagem.validadaEm !== null
            ? `Validada${passagem.validadaPorNome !== null ? ` por ${passagem.validadaPorNome}` : ''} em ${formatarDataHora(passagem.validadaEm)}.`
            : 'Aguardando validação da coordenação.'}
        </p>
      </div>

      {passagem.validadaEm === null && !dados.souParte ? (
        <Button
          variant="outlined"
          size="small"
          disabled={validar.isPending}
          onClick={() => {
            setUltimoGesto('validar');
            validar.mutate();
          }}
        >
          Validar passagem
        </Button>
      ) : null}

      {passagem.divergencias.length > 0 ? (
        <ul aria-label="Divergências da passagem" className="m-0 list-none space-y-2 pl-0">
          {passagem.divergencias.map((d) => (
            <LinhaDaDivergencia
              key={d.id}
              divergencia={d}
              corrigindo={corrigir.isPending}
              aoCorrigir={() => {
                setUltimoGesto('corrigir');
                corrigir.mutate(d.id);
              }}
            />
          ))}
        </ul>
      ) : null}

      {erro !== null ? (
        <p role="alert" className="m-0 text-[0.8125rem] text-[var(--roxo)]">
          {mensagemDoErro(erro, 'Não foi possível gravar. Tente novamente.')}
        </p>
      ) : null}
    </div>
  );
}

/**
 * O painel inteiro. Com `soSeHouver` (o Caso encerrado), só aparece quando houve passagem: num Caso recusado e
 * encerrado, um painel dizendo "registra-se com o Caso autorizado" seria ruído.
 */
export function PainelDaPassagemDePlantao({
  casoId,
  soSeHouver = false,
}: {
  casoId: string;
  soSeHouver?: boolean;
}): React.JSX.Element | null {
  const consulta = useQuery(passagemDoCaso(casoId));
  if (soSeHouver && (consulta.isPending || consulta.data?.passagem == null)) return null;
  return (
    <Painel
      titulo="Passagem de plantão"
      sub="Quem recebe confere o pedido com o IW e registra que recebeu. Divergência não bloqueia: vira pedido de ajuste para a captação."
    >
      <PassagemDePlantao casoId={casoId} />
    </Painel>
  );
}

export function PassagemDePlantao({ casoId }: { casoId: string }): React.JSX.Element {
  const consulta = useQuery(passagemDoCaso(casoId));

  if (consulta.isPending) return <EstadoDeCarregamento linhas={2} />;
  if (consulta.isError) {
    return (
      <EstadoDeErro
        erro={consulta.error}
        contexto="Não foi possível carregar a passagem de plantão"
        aoReprocessar={() => {
          void consulta.refetch();
        }}
      />
    );
  }

  const dados = consulta.data;
  return (
    <div className="space-y-4">
      {dados.processoInicial !== null && dados.processoInicial.length > 0 ? (
        <ProcessoInicial itens={dados.processoInicial} />
      ) : null}
      <ConteudoDaPassagem casoId={casoId} dados={dados} />
    </div>
  );
}

function ConteudoDaPassagem({ casoId, dados }: { casoId: string; dados: PassagemDoCaso }): React.JSX.Element {
  if (dados.passagem !== null) {
    return <PassagemRegistrada casoId={casoId} dados={dados} passagem={dados.passagem} />;
  }
  if (dados.casoEhSeu) {
    // O servidor devolveria HANDOFF_SELF: o formulário nem aparece (`D9`).
    return <p className="m-0 text-[0.8125rem] text-[var(--tinta-2)]">O Caso já é seu: não há passagem de plantão para registrar.</p>;
  }
  if (!dados.podeRegistrar) {
    return <p className="m-0 text-[0.8125rem] text-[var(--tinta-2)]">A passagem de plantão é registrada com o Caso autorizado.</p>;
  }
  return <FormularioDaPassagem casoId={casoId} dados={dados} />;
}

/** O Processo Inicial do checklist do Caso (`D11`): só mostra, não trava nem vira divergência. */
function ProcessoInicial({ itens }: { itens: readonly ItemDoProcessoInicial[] }): React.JSX.Element {
  const presentes = itens.filter((i) => i.presente).length;
  return (
    <div className="text-[0.8125rem]">
      <p className="m-0">
        <strong>Processo Inicial:</strong> {presentes} de {itens.length} {itens.length === 1 ? 'documento' : 'documentos'} presentes.
      </p>
      <ul aria-label="Processo Inicial" className="m-0 list-none pl-0 text-[var(--tinta-2)]">
        {itens.map((i) => (
          <li key={i.chave}>
            {i.rotulo} · {i.presente ? 'presente' : 'a montar'}
          </li>
        ))}
      </ul>
    </div>
  );
}
